import { MakeLogicType, connect, kea, path, props } from 'kea'
import { forms } from 'kea-forms'
import type { DeepPartial, DeepPartialMap, FieldName, ValidationErrorType } from 'kea-forms'

import api from 'lib/api'
import { integrationsLogic } from 'lib/integrations/integrationsLogic'
import { lemonToast } from 'lib/lemon-ui/LemonToast'

import { IntegrationType } from '~/types'

import {
    PushIdentityVerificationMode,
    pushIdentityPublicKeysPayload,
    pushIdentityVerificationPayload,
    resolvePushIdentityPublicKey,
    resolvePushIdentityVerification,
} from '../PushIdentityVerificationField'

export interface FCMSetupModalLogicProps {
    integration?: IntegrationType | null
    onComplete: (integrationId?: number) => void
    onClose: () => void
}

export interface FCMFormType {
    serviceAccountKey: string
    identityVerification: PushIdentityVerificationMode
    identityPublicKey: string
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface fcmSetupModalLogicValues {
    fcmIntegration: {
        identityPublicKey: string
        identityVerification: PushIdentityVerificationMode
        serviceAccountKey: string
    }
    fcmIntegrationAllErrors: Record<string, any>
    fcmIntegrationChanged: boolean
    fcmIntegrationErrors: DeepPartialMap<
        {
            identityPublicKey: string
            identityVerification: PushIdentityVerificationMode
            serviceAccountKey: string
        },
        ValidationErrorType
    >
    fcmIntegrationHasErrors: boolean
    fcmIntegrationManualErrors: Record<string, any>
    fcmIntegrationTouched: boolean
    fcmIntegrationTouches: Record<string, boolean>
    fcmIntegrationValidationErrors: DeepPartialMap<
        {
            identityPublicKey: string
            identityVerification: PushIdentityVerificationMode
            serviceAccountKey: string
        },
        ValidationErrorType
    >
    isFcmIntegrationSubmitting: boolean
    isFcmIntegrationValid: boolean
    showFcmIntegrationErrors: boolean
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface fcmSetupModalLogicActions {
    loadIntegrations: () => any // integrationsLogic
    resetFcmIntegration: (values?: {
        identityPublicKey: string
        identityVerification: PushIdentityVerificationMode
        serviceAccountKey: string
    }) => {
        values?: {
            identityPublicKey: string
            identityVerification: PushIdentityVerificationMode
            serviceAccountKey: string
        }
    }
    setFcmIntegrationManualErrors: (errors: Record<string, any>) => {
        errors: Record<string, any>
    }
    setFcmIntegrationValue: (
        key: FieldName,
        value: any
    ) => {
        name: FieldName
        value: any
    }
    setFcmIntegrationValues: (
        values: DeepPartial<{
            identityPublicKey: string
            identityVerification: PushIdentityVerificationMode
            serviceAccountKey: string
        }>
    ) => {
        values: DeepPartial<{
            identityPublicKey: string
            identityVerification: PushIdentityVerificationMode
            serviceAccountKey: string
        }>
    }
    submitFcmIntegration: () => {
        value: boolean
    }
    submitFcmIntegrationFailure: (
        error: Error,
        errors: Record<string, any>
    ) => {
        error: Error
        errors: Record<string, any>
    }
    submitFcmIntegrationRequest: (fcmIntegration: {
        identityPublicKey: string
        identityVerification: PushIdentityVerificationMode
        serviceAccountKey: string
    }) => {
        fcmIntegration: {
            identityPublicKey: string
            identityVerification: PushIdentityVerificationMode
            serviceAccountKey: string
        }
    }
    submitFcmIntegrationSuccess: (fcmIntegration: {
        identityPublicKey: string
        identityVerification: PushIdentityVerificationMode
        serviceAccountKey: string
    }) => {
        fcmIntegration: {
            identityPublicKey: string
            identityVerification: PushIdentityVerificationMode
            serviceAccountKey: string
        }
    }
    touchFcmIntegrationField: (key: string) => {
        key: string
    }
}

export type fcmSetupModalLogicType = MakeLogicType<
    fcmSetupModalLogicValues,
    fcmSetupModalLogicActions,
    FCMSetupModalLogicProps
>

export const fcmSetupModalLogic = kea<fcmSetupModalLogicType>([
    path(['products', 'workflows', 'frontend', 'FCMSetup', 'fcmSetupModalLogic']),
    props({} as FCMSetupModalLogicProps),
    connect(() => ({
        actions: [integrationsLogic, ['loadIntegrations']],
    })),
    forms(({ props, actions, values }) => ({
        fcmIntegration: {
            defaults: {
                serviceAccountKey: '',
                identityVerification: resolvePushIdentityVerification(props.integration),
                identityPublicKey: resolvePushIdentityPublicKey(props.integration),
            },
            errors: ({ serviceAccountKey }) => ({
                serviceAccountKey: serviceAccountKey.trim() ? undefined : 'Service account key is required',
            }),
            submit: async () => {
                let keyInfo: Record<string, unknown>
                try {
                    keyInfo = JSON.parse(values.fcmIntegration.serviceAccountKey)
                } catch {
                    lemonToast.error('Invalid JSON in service account key')
                    throw new Error('Invalid JSON')
                }

                try {
                    const integration = await api.integrations.create({
                        kind: 'firebase',
                        config: {
                            key_info: keyInfo,
                            ...pushIdentityVerificationPayload(
                                values.fcmIntegration.identityVerification,
                                props.integration
                            ),
                            ...pushIdentityPublicKeysPayload(
                                values.fcmIntegration.identityPublicKey,
                                props.integration
                            ),
                        },
                    })
                    actions.loadIntegrations()
                    lemonToast.success('Firebase Cloud Messaging channel created successfully!')
                    props.onComplete(integration.id)
                } catch (error: any) {
                    lemonToast.error(error.detail || 'Failed to create Firebase channel')
                    throw error
                }
            },
        },
    })),
])
